<template>
  <div class="public-video-page">
    <!-- 头部导航 -->
    <CommonHeader />

    <!-- 主体内容 -->
    <div class="main-container">
      <!-- 左侧边栏 -->
      <aside class="sidebar">
        <div class="sidebar-content">
          <!-- 分类 -->
          <div class="sidebar-section">
            <div class="sidebar-section-header">
              <h3>分类</h3>
            </div>
            <div class="category-list">
              <a
                href="javascript:;"
                :class="['category-item', { active: selectedCategory === '' }]"
                @click="selectCategory('')"
              >
                <i class="el-icon-grid"></i>
                <span>全部</span>
              </a>
              <a
                v-for="category in categoryList"
                :key="category.id"
                href="javascript:;"
                :class="['category-item', { active: selectedCategory === category.id }]"
                @click="selectCategory(category.id)"
              >
                <i class="el-icon-folder"></i>
                <span>{{ category.name }}</span>
              </a>
            </div>
          </div>

          <div class="divider-line"></div>

          <!-- 热门标签 -->
          <div class="sidebar-section">
            <h3>热门标签</h3>
            <div class="tags-list">
              <span
                v-for="tag in hotTags"
                :key="tag.id"
                :class="['tag', { active: selectedTag === tag.id }]"
                @click="selectTag(tag.id)"
              >
                {{ tag.name }}
              </span>
            </div>
          </div>

          <!-- 帮助卡片 -->
          <div class="help-card">
            <div class="help-icon">
              <i class="el-icon-s-flag"></i>
            </div>
            <p class="help-subtitle">需要帮助?</p>
            <p class="help-title">申请部门培训新主题。</p>
            <button class="help-btn">提交申请</button>
          </div>
        </div>
      </aside>

      <!-- 主要内容区 -->
      <main
        class="main-content"
        v-loading="loading"
        element-loading-text="加载中..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(255, 255, 255, 0.8)"
      >
        <div class="content-header">
          <div>
            <h1 class="page-title">公区视频库</h1>
            <p class="page-subtitle">探索 {{ totalVideos }} 个培训视频和资源</p>
          </div>
          <!-- <button class="filter-btn-mobile">
            <i class="el-icon-s-unfold"></i>
            筛选
          </button> -->
        </div>

        <!-- 搜索和筛选区 -->
        <div class="search-filter-box">
          <div class="search-input-wrapper">
            <i class="el-icon-search search-icon"></i>
            <input
              type="text"
              placeholder="搜索视频标题或描述"
              v-model="searchKey"
            />
          </div>
          <div class="divider-vertical"></div>
          <div class="sort-wrapper">
            <span class="sort-label">排序:</span>
            <div class="sort-select">
              <select v-model="sortType">
                <option value="latest">最新</option>
                <option value="hot">最热</option>
              </select>
              <i class="el-icon-arrow-down"></i>
            </div>
          </div>
          <button class="search-btn" @click="handleSearch">搜索</button>
        </div>

        <!-- 视频列表 -->
        <div class="video-grid">
          <div
            v-for="video in displayedVideos"
            :key="video.id"
            class="video-card"
            @click="goToDetail(video.id)"
          >
            <div class="video-thumbnail">
              <div class="video-bg" :style="{ backgroundImage: `url(${video.thumbnail})` }"></div>
              <div class="video-watch-status">
                <WatchStatusBadge :status="video.watchStatus" />
              </div>
              <div class="video-overlay">
                <div class="play-btn">
                  <i class="el-icon-video-play"></i>
                </div>
              </div>
              <div class="video-duration">{{ video.duration }}</div>
            </div>
            <div class="video-info">
              <h3 class="video-title">{{ video.title }}</h3>
              <div class="video-meta">
                <div class="video-department">
                  <i :class="getIconClass(video.icon)"></i>
                  <span>{{ video.department }}</span>
                </div>
                <div class="video-views">
                  <i class="el-icon-view"></i>
                  <span>{{ video.views }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <i class="el-icon-arrow-left"></i>
          </button>
          <button
            v-for="page in paginationPages"
            :key="page"
            :class="['pagination-btn', { active: currentPage === page }]"
            @click="changePage(page)"
          >
            {{ page === '...' ? '...' : page }}
          </button>
          <button class="pagination-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { getApeClassificationList, getApeTagByClassificationId, getVideoManagePage } from '@/api/api'
import CommonHeader from '@/components/header/CommonHeader.vue'
import WatchStatusBadge from '@/components/video/WatchStatusBadge.vue'
import { consumePageState, restoreWindowScroll, savePageState } from '@/utils/pageState'
import { DEFAULT_VIDEO_PLACEHOLDER } from '@/utils/placeholders'

const PUBLIC_VIDEO_PAGE_STATE_KEY = 'public-video-page'

export default {
  name: 'PublicVideo',
  components: {
    CommonHeader,
    WatchStatusBadge
  },
  data() {
    return {
      searchKey: '',
      sortType: 'latest',
      selectedCategory: '',
      selectedTag: '',
      currentPage: 1,
      pageSize: 12,
      isRestoringPageState: false,
      pendingScrollTop: null,
      loading: false, // 加载状态

      // 分类列表
      categoryList: [],

      // 热门标签（从接口获取）
      hotTags: [],

      // 视频总数（从接口获取）
      totalVideoCount: 0,

      // 视频列表（从接口获取）
      allVideos: []
    }
  },
  computed: {
    // 当前页显示的视频（直接使用allVideos，因为分页由后端处理）
    displayedVideos() {
      return this.allVideos
    },

    // 总页数（使用接口返回的总数计算）
    totalPages() {
      return Math.ceil(this.totalVideoCount / this.pageSize)
    },

    // 总视频数
    totalVideos() {
      return this.totalVideoCount
    },

    // 分页按钮
    paginationPages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, '...', total)
        } else if (current >= total - 2) {
          pages.push(1, '...', total - 2, total - 1, total)
        } else {
          pages.push(1, '...', current, '...', total)
        }
      }

      return pages
    }
  },
  watch: {
    // 监听排序类型变化
    sortType() {
      if (this.isRestoringPageState) {
        return
      }
      this.currentPage = 1
      this.fetchVideoList()
    }
  },
  mounted() {
    this.restorePageState()
    // 页面加载时获取分类列表
    this.fetchCategoryList()
    // 页面加载时获取热门标签（传空串获取全部）
    this.fetchHotTags(this.selectedCategory || '')
    // 页面加载时获取视频列表
    this.fetchVideoList()
  },
  methods: {
    restorePageState() {
      const savedState = consumePageState(PUBLIC_VIDEO_PAGE_STATE_KEY)
      if (!savedState) {
        return
      }

      this.isRestoringPageState = true
      this.searchKey = savedState.searchKey || ''
      this.sortType = savedState.sortType || 'latest'
      this.selectedCategory = savedState.selectedCategory || ''
      this.selectedTag = savedState.selectedTag || ''
      this.currentPage = savedState.currentPage || 1
      this.pendingScrollTop = Number.isFinite(savedState.scrollTop) ? savedState.scrollTop : 0
      this.$nextTick(() => {
        this.isRestoringPageState = false
      })
    },
    saveCurrentPageState() {
      savePageState(PUBLIC_VIDEO_PAGE_STATE_KEY, {
        searchKey: this.searchKey,
        sortType: this.sortType,
        selectedCategory: this.selectedCategory,
        selectedTag: this.selectedTag,
        currentPage: this.currentPage,
        scrollTop: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      })
    },
    restoreSavedScroll() {
      if (this.pendingScrollTop === null) {
        return
      }

      restoreWindowScroll(this.pendingScrollTop)
      this.pendingScrollTop = null
    },
    // 获取分类列表
    async fetchCategoryList() {
      try {
        const response = await getApeClassificationList()
        if (response.code === 1000) {
          this.categoryList = response.data || []
        }
      } catch (error) {
        console.error('获取分类列表失败:', error)
      }
    },

    // 获取热门标签
    async fetchHotTags(classificationId) {
      try {
        const params = { classificationId: classificationId || '' }
        const response = await getApeTagByClassificationId(params)
        if (response.code === 1000) {
          this.hotTags = response.data || []
        }
      } catch (error) {
        console.error('获取热门标签失败:', error)
      }
    },

    // 获取视频列表
    async fetchVideoList() {
      try {
        this.loading = true
        const params = {
          pageNumber: this.currentPage,
          pageSize: this.pageSize,
          videoTitle: this.searchKey || '',
          videoDesc: this.searchKey || '',
          classificationId: this.selectedCategory || '',
          tagId: this.selectedTag || '',
          sortField: this.sortType === 'hot' ? 'view_count' : 'create_time',
          sortOrder: 'desc',
          visibility: 0
        }
        const response = await getVideoManagePage(params)
        if (response.code === 1000 && response.data) {
          // 映射接口数据到页面需要的格式
          this.allVideos = (response.data.records || []).map(video => ({
            id: video.videoId,
            title: video.videoTitle,
            thumbnail: this.getImageUrl(video.videoCover),
            duration: this.formatDuration(video.duration),
            department: video.categoryName || '未分类',
            icon: this.getCategoryIcon(video.categoryName),
            views: this.formatNumber(video.viewCount || 0),
            category: video.categoryName,
            tags: video.tagNames ? video.tagNames.split(',').map(tag => '#' + tag.trim()) : [],
            watchStatus: video.watchStatus || '待观看'
          }))
          this.totalVideoCount = response.data.total || 0
        }
      } catch (error) {
        console.error('获取视频列表失败:', error)
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.restoreSavedScroll()
        })
      }
    },

    selectCategory(category) {
      this.selectedCategory = category
      this.currentPage = 1
      // 切换分类时重新获取对应的热门标签
      this.fetchHotTags(category || '')
      // 切换分类时重新获取视频列表
      this.fetchVideoList()
    },

    selectTag(tag) {
      if (this.selectedTag === tag) {
        this.selectedTag = ''
      } else {
        this.selectedTag = tag
      }
      this.currentPage = 1
      // 切换标签时重新获取视频列表
      this.fetchVideoList()
    },

    handleSearch() {
      this.currentPage = 1
      // 搜索时重新获取视频列表
      this.fetchVideoList()
    },

    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) {
        return
      }
      this.currentPage = page
      // 切换页码时重新获取视频列表
      this.fetchVideoList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    goToDetail(id) {
      this.saveCurrentPageState()
      // 跳转到视频详情页面，标记来自公区视频库
      this.$router.push({
        path: '/videoPlay',
        query: { id: id, from: 'public' }
      })
    },

    // ========== 数据格式化方法 ==========
    // 获取图片URL
    getImageUrl(path) {
      if (!path) return DEFAULT_VIDEO_PLACEHOLDER
      if (path.startsWith('http')) return path
      return this.$store.state.HOST + path
    },

    // 格式化时长（秒转为 mm:ss 格式）
    formatDuration(seconds) {
      if (!seconds) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    // 格式化数字
    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k'
      }
      return num.toString()
    },

    // 根据分类名获取图标
    getCategoryIcon(categoryName) {
      const iconMap = {
        'Spring': 'code',
        'Mybatis': 'database',
        'Vue': 'web',
        'React': 'layers',
        'Java': 'terminal',
        'Python': 'analytics',
        'JavaScript': 'javascript',
        '前端': 'palette',
        '后端': 'storage',
        '数据库': 'folder',
        '算法': 'calculate'
      }
      return iconMap[categoryName] || 'play_circle'
    },

    // 将动态图标名称映射到 Element UI 图标类名
    getIconClass(iconName) {
      const iconMap = {
        'code': 'el-icon-document',
        'database': 'el-icon-data-analysis',
        'web': 'el-icon-connection',
        'layers': 'el-icon-s-check',
        'terminal': 'el-icon-folder',
        'analytics': 'el-icon-data-analysis',
        'javascript': 'el-icon-document',
        'palette': 'el-icon-picture',
        'storage': 'el-icon-data-board',
        '数据库': 'el-icon-data-analysis',
        'play_circle': 'el-icon-video-play'
      }
      return iconMap[iconName] || 'el-icon-folder'
    }
  }
}
</script>

<style scoped>
.public-video-page {
  min-height: 100vh;
  background: #f0f2f4;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

/* 主容器 */
.main-container {
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
}

/* 侧边栏 */
.sidebar {
  width: 288px;
  background: white;
  border-right: 1px solid #e5e7eb;
  height: calc(100vh - 65px);
  position: sticky;
  top: 65px;
  overflow-y: auto;
}

.sidebar-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-section h3 {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s;
}

.category-item:hover {
  background: #f9fafb;
}

.category-item.active {
  background: rgba(26, 113, 255, 0.05);
  color: #1a71ff;
}

.divider-line {
  height: 1px;
  background: #f3f4f6;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 4px;
}

.tag {
  padding: 4px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.tag:hover {
  border-color: #1a71ff;
  color: #1a71ff;
}

.tag.active {
  background: #1a71ff;
  border-color: #1a71ff;
  color: white;
}

.help-card {
  position: relative;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  border-radius: 12px;
  padding: 16px;
  color: white;
  overflow: hidden;
  margin-top: auto;
}

.help-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0.2;
}

.help-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #d1d5db;
  margin: 0 0 4px 0;
}

.help-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.help-btn {
  font-size: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.help-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* 主内容区 */
.main-content {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 400px;
  padding: 32px 40px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}

.page-title {
  font-size: 36px;
  font-weight: 900;
  color: #101318;
  margin: 0;
  letter-spacing: -0.025em;
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 4px 0 0 0;
}

.filter-btn-mobile {
  display: none;
}

/* 搜索筛选框 */
.search-filter-box {
  background: white;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
}

.search-input-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 20px;
  transition: color 0.2s;
}

.search-input-wrapper:focus-within .search-icon {
  color: #1a71ff;
}

.search-input-wrapper input {
  width: 100%;
  padding: 12px 12px 12px 44px;
  border: none;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.search-input-wrapper input:focus {
  background: white;
  box-shadow: 0 0 0 2px rgba(26, 113, 255, 0.1);
}

.divider-vertical {
  width: 1px;
  height: 32px;
  background: #e5e7eb;
}

.sort-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
}

.sort-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.sort-select {
  position: relative;
}

.sort-select select {
  appearance: none;
  background: transparent;
  border: none;
  padding: 8px 28px 8px 8px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  cursor: pointer;
  outline: none;
}

.search-btn {
  padding: 12px 24px;
  background: #1a71ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(26, 113, 255, 0.2);
}

.search-btn:hover {
  background: #1557d0;
}

.search-btn:active {
  transform: translateY(1px);
}

/* 视频网格 */
.video-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 48px;
}

/* 响应式网格 */
@media (max-width: 1024px) {
  .video-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .video-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .video-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .video-card {
    border-radius: 6px;
  }

  .video-info {
    padding: 0.75rem;
  }

  .video-title {
    font-size: 14px;
  }

  .video-meta {
    font-size: 11px;
  }
}

.video-card {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.video-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 宽高比 */
  overflow: hidden;
  background-color: #e5e7eb;
}

.video-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-color: #e5e7eb;
  transition: transform 0.7s;
}

.video-card:hover .video-bg {
  transform: scale(1.05);
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
}

.video-card:hover .video-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4));
}

.play-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transform: translateY(16px);
  transition: all 0.3s;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.video-card:hover .play-btn {
  opacity: 1;
  transform: translateY(0);
}

.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.05em;
}

.video-watch-status {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.video-info {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
}

.video-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.2s;
}

.video-card:hover .video-title {
  color: #1a71ff;
}

.video-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid #f9fafb;
  font-size: 12px;
  color: #6b7280;
}

.video-department,
.video-views {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 分页 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 48px;
  margin-bottom: 48px;
}

.pagination-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f9fafb;
}

.pagination-btn.active {
  background: #1a71ff;
  border-color: #1a71ff;
  color: white;
  font-weight: 700;
  box-shadow: 0 4px 6px rgba(26, 113, 255, 0.2);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 响应式 */
@media (max-width: 1024px) {
  .sidebar {
    display: none;
  }

  .nav-menu {
    display: none;
  }

  .filter-btn-mobile {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
  }
}

@media (max-width: 768px) {
  .search-box-header {
    display: none;
  }

  .main-content {
    padding: 24px 16px;
  }

  .page-title {
    font-size: 28px;
  }
}
</style>
